import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { OutcomeConvergenceAssessment } from '@/lib/api';

const CATEGORIES = [
  ['Source Independence', 'sourceIndependence', 8],
  ['Evidence Quality', 'evidenceQuality', 10],
  ['Risk Flags', 'riskFlags', 10],
  ['Manual Assessment', 'manual', 10],
] as const;

function isExternalLink(link: string): boolean {
  return /^https?:\/\//i.test(link);
}

export function ConvergenceBreakdownInline({
  assessment,
  availability,
}: {
  assessment?: OutcomeConvergenceAssessment;
  availability?: 'available' | 'missing' | 'invalid';
}): React.ReactElement {
  if (!assessment?.breakdown) {
    return (
      <div className="mt-1 text-xs font-normal text-text-tertiary">
        {availability === 'invalid'
          ? 'Category assessment invalid'
          : availability === 'available'
            ? 'Category breakdown unavailable'
            : 'No category assessment'}
      </div>
    );
  }
  return (
    <div
      className="mt-1 text-xs font-normal text-text-tertiary"
      aria-label="Convergence category breakdown"
    >
      Source Independence {assessment.breakdown.sourceIndependence}/8 · Evidence Quality{' '}
      {assessment.breakdown.evidenceQuality}/10 · Risk Flags {assessment.breakdown.riskFlags}/10 ·
      Convergence Assessment {assessment.breakdown.manual}/10
    </div>
  );
}

export function ConvergenceBreakdownCard({
  assessment,
  availability,
  error,
}: {
  assessment?: OutcomeConvergenceAssessment;
  availability?: 'available' | 'missing' | 'invalid';
  error?: string;
}): React.ReactElement {
  return (
    <Card className="border-border/70 bg-surface/90 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <span>Convergence Assessment</span>
          <Badge
            variant={assessment?.manualAssessment.status === 'reviewed' ? 'default' : 'secondary'}
          >
            {assessment?.manualAssessment.status ??
              (availability === 'invalid' ? 'invalid' : 'unavailable')}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!assessment ? (
          <p
            className="text-sm text-text-tertiary"
            role={availability === 'invalid' ? 'alert' : undefined}
          >
            {availability === 'invalid'
              ? `Category assessment data is invalid${error ? `: ${error}` : '.'}`
              : 'No category assessment recorded.'}
          </p>
        ) : (
          <>
            {assessment.breakdown ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {CATEGORIES.map(([label, key, maximum]) => (
                  <div
                    key={key}
                    className="rounded-md border border-border bg-surface-elevated/50 p-3"
                  >
                    <div className="text-xs text-text-secondary">{label}</div>
                    <div className="mt-1 text-lg font-semibold text-text-primary">
                      {assessment.breakdown?.[key]}/{maximum}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-tertiary">
                Category breakdown unavailable for this score-only assessment.
              </p>
            )}

            <div className="text-xs text-text-secondary">
              Evidence: {assessment.evidence.criteriaAddressed}/{assessment.evidence.totalCriteria}{' '}
              criteria; {assessment.evidence.testResults.length} test layer(s)
            </div>

            {assessment.evidence.links.length > 0 && (
              <ul className="space-y-1 text-xs" aria-label="Convergence evidence">
                {assessment.evidence.links.map(link => (
                  <li key={link} className="truncate">
                    {isExternalLink(link) ? (
                      <a
                        className="text-primary underline underline-offset-2"
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {link}
                      </a>
                    ) : (
                      <code className="text-text-secondary">{link}</code>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
